import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useConfig, configContext } from "./useConfig";

const EditorConfigContext = createContext();

export function useEditorConfig() {
   return useContext(EditorConfigContext);
}

// Provider that overrides the config context with edited values
// This makes all child components (ViewerUI, Hotspot, etc.) see the live edits
export function EditorConfigOverrideProvider({ config, children }) {
   const [, , languages] = useConfig();
   
   // Dummy setConfig that does nothing (edits go through EditorConfigContext instead)
   const setConfig = useCallback(() => {}, []);
   
   // Memoize the context value to ensure it changes when config changes
   const contextValue = useMemo(() => [config, setConfig, languages], [config, setConfig, languages]);
   
   return (
      <configContext.Provider value={contextValue}>
         {children}
      </configContext.Provider>
   );
}

export function EditorConfigProvider({ children }) {
   const [originalConfig] = useConfig();
   const [editedConfig, setEditedConfig] = useState(null);
   const [isDirty, setIsDirty] = useState(false);
   const initializedRef = useRef(false);

   // Initialize edited config from original (only once)
   useEffect(() => {
      if (originalConfig && !initializedRef.current) {
         setEditedConfig({ ...originalConfig });
         initializedRef.current = true;
      }
   }, [originalConfig]);

   // Apply CSS custom properties whenever editedConfig changes
   useEffect(() => {
      if (!editedConfig) return;
      
      const documentRoot = document.documentElement;
      
      // Update CSS custom properties for live preview
      if (editedConfig['brand-color']) {
         documentRoot.style.setProperty('--brand-color', editedConfig['brand-color']);
      }
      if (editedConfig['hotspot-color']) {
         documentRoot.style.setProperty('--hotspot-color', editedConfig['hotspot-color']);
      }
      if (editedConfig['background-color']) {
         documentRoot.style.setProperty('--background-color', editedConfig['background-color']);
      }
      if (editedConfig['title-color']) {
         documentRoot.style.setProperty('--title-color', editedConfig['title-color']);
      }
   }, [editedConfig]);

   // Update a single config property
   const updateConfig = useCallback((key, value) => {
      setEditedConfig(prev => {
         const newConfig = { ...prev, [key]: value };
         setIsDirty(true);
         return newConfig;
      });
   }, []);

   // Update nested config property (e.g., actions.ar)
   const updateNestedConfig = useCallback((parentKey, childKey, value) => {
      setEditedConfig(prev => {
         const newConfig = {
            ...prev,
            [parentKey]: {
               ...prev[parentKey],
               [childKey]: value
            }
         };
         setIsDirty(true);
         return newConfig;
      });
   }, []);

   // Update deeply nested config (e.g., dimensionsConfig.enabled)
   const updateDeepConfig = useCallback((path, value) => {
      setEditedConfig(prev => {
         const keys = path.split('.');
         const newConfig = { ...prev };
         let current = newConfig;
         
         for (let i = 0; i < keys.length - 1; i++) {
            current[keys[i]] = { ...current[keys[i]] };
            current = current[keys[i]];
         }
         
         current[keys[keys.length - 1]] = value;
         setIsDirty(true);
         return newConfig;
      });
   }, []);

   // Update array item (e.g., customLogos[0])
   const updateArrayItem = useCallback((arrayKey, index, newItem) => {
      setEditedConfig(prev => {
         const newArray = [...(prev[arrayKey] || [])];
         newArray[index] = { ...newArray[index], ...newItem };
         setIsDirty(true);
         return { ...prev, [arrayKey]: newArray };
      });
   }, []);

   // Add item to array
   const addArrayItem = useCallback((arrayKey, newItem) => {
      setEditedConfig(prev => {
         const newArray = [...(prev[arrayKey] || []), newItem];
         setIsDirty(true);
         return { ...prev, [arrayKey]: newArray };
      });
   }, []);

   // Remove item from array
   const removeArrayItem = useCallback((arrayKey, index) => {
      setEditedConfig(prev => {
         const newArray = (prev[arrayKey] || []).filter((_, i) => i !== index);
         setIsDirty(true);
         return { ...prev, [arrayKey]: newArray };
      });
   }, []);

   // Reset to original config
   const resetConfig = useCallback(() => {
      if (originalConfig) {
         setEditedConfig({ ...originalConfig });
         setIsDirty(false);
      }
   }, [originalConfig]);

   // Export config as JSON
   const exportConfig = useCallback(() => {
      if (!editedConfig) return "{}";
      
      // Create a clean copy without internal properties
      const cleanConfig = { ...editedConfig };
      return JSON.stringify(cleanConfig, null, 3);
   }, [editedConfig]);

   return (
      <EditorConfigContext.Provider
         value={{
            config: editedConfig,
            originalConfig,
            isDirty,
            updateConfig,
            updateNestedConfig,
            updateDeepConfig,
            updateArrayItem,
            addArrayItem,
            removeArrayItem,
            resetConfig,
            exportConfig,
         }}
      >
         {children}
      </EditorConfigContext.Provider>
   );
}

